import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getNavigationMenu } from '../../../contentful/queries';
import { getFieldValue, mapNavigationItem } from '../../../contentful/mappers';
import logo from '../../../assets/logo-with-white-bg.svg';
import NavLinks from './NavLinks';
import './Header.css';

function Header() {
  const location = useLocation();
  const isHomePage = (location.pathname === '/' || location.pathname === '/home');
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Start with logo hidden if we're at the top of the page (hero section visible)
  // But always show on non-home pages
  const [isLogoVisible, setIsLogoVisible] = useState(() => {
    if (typeof window !== 'undefined') {
      // If not on home page, always show logo
      if (!isHomePage) {
        return false;
      }
      // Check if hero logo exists immediately
      return !!window.heroLogoElement;
    }
    return false;
  });

  useEffect(() => {
    async function loadNavigation() {
      try {
        const menu = await getNavigationMenu();
        if (menu) {
          const items = getFieldValue(menu, 'items') || [];
          setNavItems(items.map(mapNavigationItem).filter(Boolean));
        }
      } catch (error) {
        console.error('Error loading navigation:', error);
      } finally {
        setLoading(false);
      }
    }
    loadNavigation();
  }, []);

  useEffect(() => {
    // If not on home page, always show logo
    if (!isHomePage) {
      setIsLogoVisible(false);
      return;
    }

    // Check for hero logo visibility using Intersection Observer (only on home page)
    const checkLogoVisibility = () => {
      const heroLogo = window.heroLogoElement;
      if (!heroLogo) {
        // If logo doesn't exist yet, hide navbar logo (we're likely at hero section)
        setIsLogoVisible(true);
        return null;
      }

      // Check immediately if logo is in viewport
      const rect = heroLogo.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
      setIsLogoVisible(isInViewport);

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsLogoVisible(entry.isIntersecting);
          });
        },
        {
          threshold: 0, // Trigger when any part of the logo is visible
          rootMargin: '0px',
        }
      );

      observer.observe(heroLogo);

      return () => {
        observer.disconnect();
      };
    };

    // Check immediately
    const cleanup = checkLogoVisibility();
    
    // Also check after a short delay to catch logo that loads later
    const timeoutId = setTimeout(() => {
      checkLogoVisibility();
    }, 100);

    // Also check on scroll/resize
    window.addEventListener('scroll', checkLogoVisibility, { passive: true });
    window.addEventListener('resize', checkLogoVisibility);

    return () => {
      clearTimeout(timeoutId);
      if (cleanup) cleanup();
      window.removeEventListener('scroll', checkLogoVisibility);
      window.removeEventListener('resize', checkLogoVisibility);
    };
  }, [isHomePage]);


  if (loading) {
    return (
      <header className="header">
        <div className="container">
          <nav>Loading...</nav>
        </div>
      </header>
    );
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogoClick = (e) => {
    if (isHomePage) {
      // If on home page, prevent navigation and clear hash + scroll to top smoothly
      e.preventDefault();
      // Clear any hash from the URL
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
      }
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // If not on home page, scroll to top instantly when navigating
      window.scrollTo({
        top: 0,
        behavior: 'instant'
      });
      // Clear hash immediately if present
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  };

  return (
    <header className="header">
      <div className="container">
        <nav className="navbar">
          <Link 
            to="/" 
            className={`navbar-brand ${isLogoVisible ? 'navbar-brand-hidden' : ''}`}
            onClick={handleLogoClick}
          >
            <img src={logo} alt="KCGU logo" className="header-logo" />
            <span className="navbar-brand-text">King's College Graduate Union</span>
          </Link>
          <button 
            className="navbar-toggle" 
            onClick={toggleMobileMenu}
            aria-label="Toggle navigation menu"
          >
            <span className={`hamburger ${isMobileMenuOpen ? 'hamburger-open' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
          <div className={`navbar-menu ${isMobileMenuOpen ? 'navbar-menu-open' : ''}`}>
            <NavLinks items={navItems} onLinkClick={() => setIsMobileMenuOpen(false)} />
          </div>
        </nav>
      </div>
    </header>
  );
}

export default Header;

